/**
 * @fileoverview What a session you just tapped is, and what to do with it.
 *
 * The instructor's half of the calendar popup (MeetingDetailsDialog, the same
 * one every calendar opens): the program over the class or student's name,
 * when, who, where — and the ways to write it up.
 *
 * A class on the calendar is not a thing to look at, it is a thing to write up,
 * and there are exactly two ways to write one up: record it, or type it. Before
 * this, tapping a class meeting went straight to the recorder from the
 * dashboard and to a list of every meeting from the calendar — so the two
 * surfaces disagreed, and neither offered typed notes at all.
 *
 * Both routes carry the OCCURRENCE STAMP, which is what makes the lesson count:
 * a note written against the meeting reaches the program, the ledger and the
 * org, and one written from nowhere does not. That is why this hands out hrefs
 * built by `schedule.js` rather than plain class links.
 *
 * A meeting that already has notes offers to open them first. Writing a second
 * lesson against the same session is the mistake this is most likely to cause,
 * and the fix is to show the instructor what is already there. The teaching
 * view names that lesson `recorded_lesson_id`; `lesson_id` is the older name.
 *
 * **No Edit here.** An instructor cannot move or cancel a session from Hans:
 * the organization sets a program class's times, and everything else is
 * booked in Kalver Scheduler, which is where it changes. The popup says which.
 * "Open class" goes to the class page, where the session's prep notes and the
 * class's curriculum live; a session still in its prep window also offers
 * "Add prep notes" straight to that session's plan (lib/prepNotes).
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import MeetingDetailsDialog, { addressLine } from "@/components/org/MeetingDetailsDialog";
import { Button } from "@/components/studio/primitives";
import { composeHrefForOccurrence, recordHrefForOccurrence, zoneAbbreviation } from "@/lib/schedule";
import { isPrepWindow } from "@/lib/prepNotes";
import { schedulerSignInTarget } from "@/lib/schedulerSignIn";

/** Who teaches it, from the instructor's side: it is theirs unless covered. */
function teacherLine(occ) {
  if (occ.covered_by && !occ.covered_by.mine) return { substitute: occ.covered_by.name || "a substitute (to be confirmed)" };
  return { instructors: occ.covered_by?.mine || occ.covering ? "You, covering" : "You" };
}

/**
 * @param {{occurrence: object|null, onOpenChange: Function, tags?: Array}} props
 *   `occurrence` null closes it; the dialog renders nothing without one.
 *   `tags` are the calendar's state words for the row (Needs notes, …).
 */
export default function ClassSessionActions({ occurrence, onOpenChange, tags = [] }) {
  const navigate = useNavigate();
  const occ = occurrence;
  const classId = occ?.class_id;
  // The room, the venue and the class's notes come with the class, not the
  // session: read on open, and only for a class.
  const [cls, setCls] = useState(null);
  const [openingScheduler, setOpeningScheduler] = useState(false);
  const [schedulerError, setSchedulerError] = useState("");
  useEffect(() => { setSchedulerError(""); }, [occ?.occurrence_id, occ?.scheduler_appointment_id]);
  useEffect(() => {
    setCls(null);
    if (!classId) return undefined;
    const controller = new AbortController();
    api
      .get(`/classes/${classId}`, { signal: controller.signal })
      .then((r) => setCls(r.data))
      .catch(() => {});
    return () => controller.abort();
  }, [classId]);

  if (!occ) return null;

  const go = (href) => {
    onOpenChange(false);
    navigate(href);
  };

  const day = occ.lesson_date || occ.effective_date || occ.date;
  const lessonId = occ.lesson_id || occ.recorded_lesson_id;
  const coveredOther = Boolean(occ.covered_by && !occ.covered_by.mine);
  const cancelled = occ.status === "cancelled";
  const target = classId || occ.student_id;
  // `recordable === false` means the meeting cannot be recorded yet — an
  // unmapped class, a missing connection. Saying so beats a button that lands
  // on Settings with no explanation.
  const blocked = occ.recordable === false && !occ.attendance_reviewable;
  const canWriteNotes = target && (!occ.scheduler_appointment_id || (occ.notes_writable ?? !blocked));
  const schedulerReadOnly = Boolean(occ.scheduler_appointment_id && (!target || (blocked && !canWriteNotes)));
  const needsMapping = !target || occ.issues?.includes("attendee_or_class_mapping");
  const unavailableReason = needsMapping
    ? "The attendees need to be linked to the correct student or class before this lesson can be recorded."
    : occ.issues?.includes("service_program_mapping")
      ? "This lesson’s service needs to be linked to the correct program before it can be recorded."
      : "Notes aren’t available for this appointment yet. Open Scheduler to review its details.";
  const prepping = Boolean(classId) && !coveredOther && !lessonId && isPrepWindow({ ...occ, date: day });
  const orgName = cls?.org_name || occ.org_name;
  const orgSets = Boolean(classId && (cls?.org_scheduled ?? (occ.program_id && occ.org_id)));
  const openScheduler = async () => {
    setSchedulerError("");
    // Open during the click gesture so Safari does not block the later SSO navigation.
    const tab = window.open("about:blank", "_blank");
    if (!tab) {
      setSchedulerError("Allow a new tab for Hans, then try Open in Scheduler again.");
      return;
    }
    tab.opener = null;
    tab.document.title = "Opening Scheduler…";
    setOpeningScheduler(true);
    try {
      const appointmentId = occ.scheduler_appointment_id;
      const selectedAppointment = appointmentId && !appointmentId.startsWith("cs_");
      const { data } = await api.post(`/scheduler/connections/${encodeURIComponent(occ.scheduler_connection_id)}/sign-in`,
        selectedAppointment ? { destination: "appointments", appointment_id: appointmentId } : { destination: "calendar" });
      const url = schedulerSignInTarget(data, occ.business_id);
      if (!tab.closed) tab.location.replace(url);
    } catch {
      tab.close();
      setSchedulerError("Could not open Scheduler. Please try again.");
    } finally {
      setOpeningScheduler(false);
    }
  };

  const meeting = {
    title: occ.display_name || (classId ? "Class" : "Lesson"),
    program: occ.program_title || (occ.business_label && occ.business_label !== "Personal" ? occ.business_label : undefined),
    date: day,
    time_local: occ.time_local,
    duration_minutes: occ.duration_minutes,
    zone: occ.timezone ? zoneAbbreviation(occ.timezone, day) : undefined,
    status: occ.status,
    ...teacherLine(occ),
    location: addressLine(cls?.location_address),
    room: cls?.room,
    note: cls?.notes,
    attending: classId ? `Class · ${(occ.student_ids || []).length} attending` : occ.student_id ? "Private lesson" : undefined,
    tags,
  };

  let actions = null;
  if (lessonId) {
    actions = (
      <div className="flex flex-col gap-2.5">
        <Button
          icon="check"
          onPress={() => go(`/lesson/${lessonId}${classId ? "?kind=class" : ""}`)}
          data-testid="session-open-lesson"
        >
          {coveredOther ? "Open their lesson" : "Open this lesson"}
        </Button>
        <p className="font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]">
          {coveredOther ? "Their notes, to read." : "This session is already written up."}
        </p>
      </div>
    );
  } else if (schedulerReadOnly && !coveredOther && !cancelled) {
    actions = <p className="font-sans text-[13px] leading-relaxed text-[var(--text-secondary)]" role="status">{unavailableReason}</p>;
  } else if (target && !coveredOther && !cancelled) {
    actions = (
      <div className="flex flex-col gap-2.5">
        <Button variant={blocked ? "ghost" : "primary"} icon="mic" onPress={() => go(recordHrefForOccurrence(occ))} disabled={blocked} data-testid="session-record">
          Record lesson
        </Button>
        {canWriteNotes && <Button variant={blocked ? "primary" : "ghost"} icon="edit" onPress={() => go(composeHrefForOccurrence(occ))} data-testid="session-write-notes">
          Write notes
        </Button>}
        <p className="font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]">
          {blocked
            ? occ.scheduler_appointment_id
              ? "Recording isn’t available for this lesson. You can still teach it and write notes."
              : "This class can’t be recorded until its scheduling is connected — typed notes still save against the session."
            : classId
              ? "Either way the notes land on this session, so the class, the program and your ledger all see it."
              : "Either way the notes land on this session, in the student's history and your ledger."}
        </p>
      </div>
    );
  }

  return (
    <MeetingDetailsDialog
      key={occ.occurrence_id || `${target}-${day}-${occ.time_local}`}
      meeting={meeting}
      onClose={() => onOpenChange(false)}
      locked={
        orgSets
          ? `${orgName || "The organization"} sets this class's dates and times.`
          : "Change this session in Kalver Scheduler."
      }
      secondary={
        prepping
          ? {
              label: "Add prep notes",
              icon: "edit",
              testId: "session-prep",
              onPress: () =>
                go(`${composeHrefForOccurrence(occ)}&occurrence=${encodeURIComponent(occ.occurrence_id)}`),
            }
          : undefined
      }
      open={
        classId
          ? { label: "Open class", onPress: () => go(`/classes/${classId}/sessions`) }
          : occ.student_id
            ? { label: "Open student", onPress: () => go(`/students/${occ.student_id}`) }
            : undefined
      }
    >
      {actions}
      {occ.scheduler_connection_id && occ.business_id && <div className="mt-3 flex flex-col gap-2.5">
        <Button variant="ghost" onPress={openScheduler} disabled={openingScheduler}>
          {openingScheduler ? "Opening Scheduler…" : "Open in Scheduler"}
        </Button>
        {schedulerError && <p role="alert" className="font-sans text-[13px] text-[var(--state-danger)]">{schedulerError}</p>}
      </div>}
    </MeetingDetailsDialog>
  );
}
